import { z } from 'zod';

export type DataType = 'number' | 'text' | 'date';
export type InputType = 'text' | 'textarea' | 'select' | 'checkbox';

export type Field = {
  id: string;
  label: string;
  placeholder?: string;
  dataType: DataType;
  inputType: InputType;
  isMulti: boolean;
  isRequired: boolean;
  providerKey: string;
  validation: {
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    patternMessage?: string;
  };
  options?: { id: string; label: string }[];
};

// Mirrors how the API formats multi-value fields before sending them to Helix
export const normalizeMultiValue = (values: string[]): string =>
  values
    .map((value) => value.trim())
    .filter(Boolean)
    .join(',');

// Helper to create validation schema based on field
export const createFieldSchema = (field: Field) => {
  const applyMinLength = (s: z.ZodString) => {
    if (field.validation.minLength) {
      return s.min(
        field.validation.minLength,
        `${field.label} must be at least ${field.validation.minLength} characters`
      );
    }
    if (field.isRequired) {
      return s.min(1, `${field.label} is required`);
    }
    return s;
  };

  const applyMaxLength = (s: z.ZodString) =>
    field.validation.maxLength && !field.isMulti
      ? s.max(
          field.validation.maxLength,
          `${field.label} must be no more than ${field.validation.maxLength} characters`
        )
      : s;

  const applyPattern = (s: z.ZodString) =>
    field.validation.pattern
      ? s.regex(
          new RegExp(field.validation.pattern),
          field.validation.patternMessage || `Invalid ${field.label} format`
        )
      : s;

  const applyDateValidation = (s: z.ZodString) => {
    if (field.dataType !== 'date') {
      return s;
    }
    return s
      .regex(
        /^\d{4}-\d{2}-\d{2}$/,
        'Enter a valid date in the format YYYY-MM-DD'
      )
      .refine((value) => {
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(value);
        return (
          date.getUTCFullYear() === year &&
          date.getUTCMonth() + 1 === month &&
          date.getUTCDate() === day
        );
      }, 'Enter a valid date in the format YYYY-MM-DD');
  };

  // Only a select constrains the user to a fixed list, so only a select may
  // reject a value for being outside it. Options on a free-text field are
  // suggestions, not a whitelist.
  const applySelectOptions = (s: z.ZodString) => {
    const allowedOptionIds = field.options?.map(({ id }) => id) ?? [];

    if (field.inputType !== 'select' || allowedOptionIds.length === 0) {
      return s;
    }

    return s.refine(
      (value) => value === '' || allowedOptionIds.includes(value),
      `Select a valid ${field.label}`
    );
  };

  const schema = applySelectOptions(
    applyDateValidation(
      applyPattern(applyMaxLength(applyMinLength(z.string())))
    )
  );

  if (field.isMulti) {
    const { maxLength } = field.validation;
    const arraySchema = field.isRequired
      ? z.array(schema).min(1, `At least one ${field.label} is required`)
      : z.array(schema);
    const lengthCheckedSchema = maxLength
      ? arraySchema.refine(
          (values) => normalizeMultiValue(values).length <= maxLength,
          `${field.label} must be no more than ${maxLength} characters in total`
        )
      : arraySchema;

    return field.isRequired
      ? lengthCheckedSchema
      : lengthCheckedSchema.optional();
  }

  return field.isRequired ? schema : schema.optional();
};

// Type for dynamic field values
export type DynamicFieldValues = Partial<Record<string, string | string[]>>;
